import { useCallback } from 'react';

import { ResourceType } from '@setvi/shared/enums';
import { useDialog } from '@setvi/shared/providers';
import { openResourceInViewer } from '@setvi/shared/providers/resource-opener';
import type { OpenResource } from '@setvi/shared/providers/resource-opener';

import { usePrintOrderCompany } from 'componentsV2/print-order';

import { ResourcePreviewDialog } from '../components';
import { RESOURCE_PREVIEW_DIALOG_ID } from '../constants';

/**
 * How a resource click opens the resource. Print-order companies (the
 * `shoppingCart` LD flag, i.e. Alliance) get the in-app PDF preview dialog;
 * everyone else keeps the legacy web viewer in a new tab. Non-PDF resources
 * always go to the viewer — the dialog renders through react-pdf only. The
 * Outlook dialog pages behave like the web: they render in an Office dialog
 * window, where the preview dialog fits.
 *
 * The branch is decided synchronously inside the click handler: `window.open`
 * after an await is silently dropped by popup blockers, so the viewer path
 * must never wait on anything.
 */
export const useOpenResource = (): OpenResource => {
  const previewEnabled = usePrintOrderCompany();
  const { openDialog, closeDialog } = useDialog();

  return useCallback(
    props => {
      const isPdf = Number(props.resourceTypeId) === ResourceType.PdfDocument;
      // The dialog loads the PDF from the resource object or fetches it by its
      // numeric id; without either there is nothing to preview.
      const canLoad = !!props.resource || props.id != null;
      // Banner analytics and required-reading state only exist in the viewer
      // url — the dialog has no equivalent, so those clicks keep the viewer.
      const viewerOnly = !!props.banner || props.documentId != null;

      if (!previewEnabled || !isPdf || !canLoad || viewerOnly) {
        openResourceInViewer(props);
        return;
      }

      openDialog(
        <ResourcePreviewDialog
          {...props}
          onClose={() => closeDialog(RESOURCE_PREVIEW_DIALOG_ID)}
        />,
        { id: RESOURCE_PREVIEW_DIALOG_ID }
      );
    },
    [previewEnabled, openDialog, closeDialog]
  );
};
